/**
 * DealNexa API Service
 * Centralized API client with Supabase JWT authentication
 */

import axios from 'axios';
import { supabase } from '../lib/supabase';

const API_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== DOCUMENTS ====================

export const documentsApi = {
  // List all documents
  list: () => api.get('/api/documents'),
  
  // Get single document
  get: (id) => api.get(`/api/documents/${id}`),
  
  // Delete document
  delete: (id) => api.delete(`/api/documents/${id}`),
  
  // Update document category
  updateCategory: (id, category) => 
    api.put(`/api/documents/${id}/category`, { category }),
  
  // Link/unlink document to deal
  linkDeal: (id, dealId, dealName = null) => 
    api.put(`/api/documents/${id}/deal`, { dealId, dealName }),
  
  // Bulk delete documents
  bulkDelete: (documentIds) => 
    api.post('/api/documents/bulk/delete', { documentIds }),
  
  // Bulk update categories
  bulkUpdateCategory: (documentIds, category) => 
    api.post('/api/documents/bulk/category', { documentIds, category }),
  
  // Bulk link to deal
  bulkLinkDeal: (documentIds, dealId, dealName) => 
    api.post('/api/documents/bulk/deal', { documentIds, dealId, dealName }),
  
  // Upload document (uses FormData)
  upload: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post('/api/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => {
        const progress = Math.round((e.loaded * 100) / e.total);
        onProgress(progress);
      } : undefined
    });
  },
  
  // Get document preview URL
  getPreviewUrl: (id) => `${API_URL}/api/documents/${id}/preview`,
  
  // Get document text
  getText: (id) => api.get(`/api/documents/${id}/text`),
  
  // Reanalyze document
  reanalyze: (id) => api.post(`/api/documents/${id}/reanalyze`),
  
  // Analyze document
  analyze: (id) => api.post(`/api/documents/${id}/analyze`),
  
  // Analyze document
  analyze: (id) => api.post(`/api/documents/${id}/analyze`)
};

// ==================== DEALS ====================

export const dealsApi = {
  // List all deals
  list: () => api.get('/api/deals'),
  
  // Get single deal
  get: (id) => api.get(`/api/deals/${id}`),
  
  // Create deal
  create: (data) => api.post('/api/deals', data),
  
  // Update deal
  update: (id, data) => api.put(`/api/deals/${id}`, data),
  
  // Delete deal
  delete: (id) => api.delete(`/api/deals/${id}`),
  
  // Get deal documents
  getDocuments: (id) => api.get(`/api/deals/${id}/documents`),
  
  // Add document to deal
  addDocument: (dealId, jobId) => 
    api.post(`/api/deals/${dealId}/documents`, { jobId }),
  
  // Remove document from deal
  removeDocument: (dealId, docId) => 
    api.delete(`/api/deals/${dealId}/documents/${docId}`),
  
  // Get deal tasks
  getTasks: (id) => api.get(`/api/deals/${id}/tasks`),
  
  // Create task
  createTask: (dealId, data) => 
    api.post(`/api/deals/${dealId}/tasks`, data),
  
  // Update task
  updateTask: (dealId, taskId, data) => 
    api.put(`/api/deals/${dealId}/tasks/${taskId}`, data),
  
  // Delete task
  deleteTask: (dealId, taskId) => 
    api.delete(`/api/deals/${dealId}/tasks/${taskId}`),
  
  // Get deal contacts
  getContacts: (id) => api.get(`/api/deals/${id}/contacts`),
  
  // Get deal notes
  getNotes: (id) => api.get(`/api/deals/${id}/notes`),
  
  // Add note
  addNote: (dealId, content) => 
    api.post(`/api/deals/${dealId}/notes`, { content }),
  
  // Update note
  updateNote: (dealId, noteId, content) => 
    api.put(`/api/deals/${dealId}/notes/${noteId}`, { content }),
  
  // Delete note
  deleteNote: (dealId, noteId) => 
    api.delete(`/api/deals/${dealId}/notes/${noteId}`),
  
  // Get AI analysis/finding
  getAnalysis: (id) => api.get(`/api/deals/${id}/analysis`),
  
  // Trigger AI analysis
  analyze: (id) => api.post(`/api/deals/${id}/analyze`),
  
  // Get findings
  getFindings: (id) => api.get(`/api/deals/${id}/findings`),
  
  // Create finding
  createFinding: (dealId, data) => 
    api.post(`/api/deals/${dealId}/findings`, data),
  
  // Update finding
  updateFinding: (dealId, findingId, data) => 
    api.put(`/api/deals/${dealId}/findings/${findingId}`, data),
  
  // Delete finding
  deleteFinding: (dealId, findingId) => 
    api.delete(`/api/deals/${dealId}/findings/${findingId}`)
};

// ==================== CONTACTS ====================

export const contactsApi = {
  // List all contacts
  list: () => api.get('/api/contacts'),
  
  // Get single contact
  get: (id) => api.get(`/api/contacts/${id}`),
  
  // Create contact
  create: (data) => api.post('/api/contacts', data),
  
  // Update contact
  update: (id, data) => api.put(`/api/contacts/${id}`, data),
  
  // Delete contact
  delete: (id) => api.delete(`/api/contacts/${id}`),
  
  // Import contacts
  import: (contacts) => api.post('/api/contacts/import', { contacts }),
  
  // Get contacts by deal
  getByDeal: (dealId) => api.get(`/api/contacts/deal/${dealId}`)
};

// ==================== DILIGENCE ====================

export const diligenceApi = {
  // Get all requests for an organization
  list: () => api.get('/api/diligence'),
  
  // Get requests for a deal
  getByDeal: (dealId) => api.get(`/api/diligence/deal/${dealId}`),
  
  // Create request
  create: (data) => api.post('/api/diligence', data),
  
  // Update request
  update: (id, data) => api.put(`/api/diligence/${id}`, data),
  
  // Delete request
  delete: (id) => api.delete(`/api/diligence/${id}`),
  
  // Update status
  updateStatus: (id, status) => 
    api.patch(`/api/diligence/${id}/status`, { status }),
  
  // Add document to request
  addDocument: (id, documentId) => 
    api.post(`/api/diligence/${id}/documents`, { documentId }),
  
  // Remove document from request
  removeDocument: (id, documentId) => 
    api.delete(`/api/diligence/${id}/documents/${documentId}`)
};

// ==================== ANNOTATIONS ====================

export const annotationsApi = {
  // Get annotations for a document
  getByDocument: (documentId) => 
    api.get(`/api/annotations/document/${documentId}`),
  
  // Create annotation
  create: (data) => api.post('/api/annotations', data),
  
  // Update annotation
  update: (id, data) => api.put(`/api/annotations/${id}`, data),
  
  // Delete annotation
  delete: (id) => api.delete(`/api/annotations/${id}`)
};

// ==================== ORGANIZATIONS ====================

export const organizationsApi = {
  // Get current org
  getCurrent: () => api.get('/api/organizations/me'),
  
  // Get categories
  getCategories: () => api.get('/api/organizations/default/categories'),
  
  // Add category
  addCategory: (category) => 
    api.post('/api/organizations/default/categories', { category }),
  
  // Delete category
  deleteCategory: (category) => 
    api.delete(`/api/organizations/default/categories/${encodeURIComponent(category)}`),
  
  // Get org users
  getUsers: () => api.get('/api/organizations/me/users'),
  
  // Invite user
  inviteUser: (email, role) => 
    api.post('/api/organizations/me/invite', { email, role })
};

// ==================== USERS ====================

export const usersApi = {
  // Get all users (admin)
  list: () => api.get('/api/users'),
  
  // Get current user
  getMe: () => api.get('/api/auth/me'),
  
  // Update profile
  updateProfile: (data) => api.put('/api/auth/me', data)
};

// ==================== ACTIVITIES ====================

export const activitiesApi = {
  // Get recent activities
  list: (limit = 20) => api.get(`/api/activities?limit=${limit}`)
};

// ==================== CRM ====================

export const crmApi = {
  // List all CRM records
  list: () => api.get('/api/crm/records'),
  
  // Update CRM record
  update: (id, data) => api.put(`/api/crm/records/${id}`, data),
  
  // Delete CRM record
  delete: (id) => api.delete(`/api/crm/records/${id}`),
  
  // Import CRM records
  import: (records, columns, sourceName) => 
    api.post('/api/crm/import', { records, columns, sourceName }),
  
  // Push records to pipeline
  pushToPipeline: (recordIds, stage = 'level_1_assessment') => 
    api.post('/api/crm/push-to-pipeline', { recordIds, stage })
};

// ==================== AUDIT TRAIL ====================

export const auditApi = {
  // Get audit logs (admin only)
  list: (params = {}) => api.get('/api/audit/logs', { params }),
  
  // Log an audit event
  log: (action, resource, details, userName) => 
    api.post('/api/audit/log', { action, resource, details, userName })
};

// ==================== AI CHAT ====================

export const chatApi = {
  // Send message
  send: (messages, dealContext = null) => 
    api.post('/api/chat', { messages, dealContext }),
  
  // Get deal context
  getDealContext: (dealId) => api.get(`/api/chat/deal/${dealId}/context`)
};

export default api;
